import { Component, OnInit, Input, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { IUProperty } from 'src/app/shared/interface/umbraco-property';
import { HttpClient } from '@angular/common/http';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { RecaptchaComponent } from 'ng-recaptcha';
import { IPictureData } from '../lib/public_api';
import { ITitleStyleSettings } from '../../shared/interface/title-style-settings';
import { applyTitleSettings } from '../../shared/utils/title-style-settings-utils';

interface IContactFormPanelData {
  title: IUProperty<string>;
  header: IUProperty<string>;
  address: IUProperty<string>;
  altAddress: IUProperty<string>;
  email: IUProperty<string>;
  phone: IUProperty<string>;
  cvrNumber: IUProperty<string>;
  facebook: IUProperty<string>;
  linkedIn: IUProperty<string>;
  youtube: IUProperty<string>;
  backgroundImage: IUProperty<IPictureData>;

  contactFieldsFontSize: IUProperty<string>;
  contactFieldsFontWeight: IUProperty<string>;
  leftTitleSize: IUProperty<string>;
  leftTitleFontWeight: IUProperty<string>;
  rightTitleSize: IUProperty<string>;
  rightTitleFontWeight: IUProperty<string>;

  titleStyleSettings?: ITitleStyleSettings;

  bodyColor: IUProperty<string>;
  cookiePolicyPageUrl: string;
  reCaptchaSecret: string;
}

interface IContactFormPanelResponseData {
  reCaptchaFailed: boolean;
}

@Component({
  selector: 'app-contact-form-panel',
  templateUrl: './contact-form-panel.component.html',
  styleUrls: ['./contact-form-panel.component.less']
})
export class ContactFormPanelComponent implements OnInit, AfterViewInit {
  @Input() data: IContactFormPanelData;
  @ViewChild('captcha', { static: false }) captcha: RecaptchaComponent;
  @ViewChild('panelEl', { static: false }) panelElRef: ElementRef<HTMLElement>;
  @ViewChild('titleEl', { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild('leftSideEl', { static: false }) leftSideEl: ElementRef<HTMLElement>;
  @ViewChild('rightSideEl', { static: false }) rightSideEl: ElementRef<HTMLElement>;

  headerText = {
    title: this.translateService.instant("contactPanel.Header.Title"),
    subtitle: this.translateService.instant("contactPanel.Header.Subtitle")
  }
  formText = {
    name: this.translateService.instant("contactPanel.InputField.Name"),
    email: this.translateService.instant("contactPanel.InputField.Email"),
    phone: this.translateService.instant("contactPanel.InputField.Phone"),
    subject: this.translateService.instant("contactPanel.InputField.Subject"),
    message: this.translateService.instant("contactPanel.InputField.Message"),
  }
  privacyPolicy = {
    text: this.translateService.instant("contactPanel.PrivacyPolicy.Text"),
    link: this.translateService.instant("contactPanel.PrivacyPolicy.Link")
  }
  submitButton = this.translateService.instant("contactPanel.Submit.SubmitButton");
  contactGroup;
  errMsg: string = null;
  succMsg: string = null;
  captchaResponse: string = null;
  requestSuccess: boolean = false;

  constructor(private http: HttpClient,
              private hostRef: ElementRef,
              private translateService: TranslateService) { }

  ngOnInit() {
    this.contactGroup = new FormGroup({
      name: new FormControl("", Validators.required),
      email: new FormControl("", Validators.compose([Validators.required, Validators.email, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')])),
      phone: new FormControl(""),
      subject: new FormControl(""),
      message: new FormControl(""),
      privacyPolicy: new FormControl(false, Validators.requiredTrue)
    });
  }

  ngAfterViewInit() {
    this.setCustomThemeProperties(this.data);
    this.setPanelBackgroundImage(this.data);
  }

  reCaptchaValidated(response: string){
    this.captchaResponse = response;
  }

  sendRequest(event) {
    event.preventDefault();
    this.errMsg = "";

    if (!this.contactGroup.invalid && this.captchaResponse) {
      
      this.contactGroup.value.reCaptchaToken = this.captchaResponse;
      this.http.post('ubaseline/api/contactFormPanel/sendRequest', this.contactGroup.value).subscribe(
        (data:IContactFormPanelResponseData) => {
          if(data.reCaptchaFailed){
            this.errMsg = this.translateService.instant("reCaptcha.Error");
            this.resetCaptcha();
            return;
          }

           this.contactGroup.reset();
           this.successOnSend() 
          },
        error => console.log('An error has occured', error)
      )
    } else {
      if (!this.contactGroup.value.privacyPolicy) {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.PrivacyPolicy");
      } else if (this.contactGroup.controls.name.status === "INVALID") {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.MissingFields");
      } else if(!this.captchaResponse) {
        this.errMsg = this.translateService.instant("reCaptcha.CheckRecaptcha");
      } else {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.Email");
      }
    }
  }

  successOnSend() {
    this.startTimer();
    this.requestSuccess = true;
    this.resetCaptcha();
    this.succMsg = this.translateService.instant("contactFormPanel.SuccessMessage.OnSend");
  }
  
  resetCaptcha(){
    this.captcha.reset();
    this.captchaResponse = null;
  }

  startTimer() {
    let countDown;
    let timeLeft: number = 2;
    const source = timer(1000, 1000);
    countDown = source.subscribe(val => {
      timeLeft--;
      if (timeLeft === 0) {
        this.requestSuccess = false;
        countDown.unsubscribe();
      }
    });
  }

  private setCustomThemeProperties(d: IContactFormPanelData) {
    applyTitleSettings(d, this.titleEl);

    if (d.bodyColor && d.bodyColor.value) {
      this.hostRef.nativeElement.style.setProperty('--contact-form-body-color', d.bodyColor.value);
    }
    if (d.contactFieldsFontSize && d.contactFieldsFontSize.value) {
      this.rightSideEl.nativeElement.style.setProperty('--contact-fields-font-size', d.contactFieldsFontSize.value + 'px');
    }
    if (d.contactFieldsFontWeight && d.contactFieldsFontWeight.value) {
      this.rightSideEl.nativeElement.style.setProperty('--contact-fields-font-weight', d.contactFieldsFontWeight.value);
    }
    if (d.leftTitleSize && d.leftTitleSize.value) {
      this.leftSideEl.nativeElement.style.setProperty('--contact-form-left-title-size', d.leftTitleSize.value + 'px');
    }
    if (d.leftTitleFontWeight && d.leftTitleFontWeight.value) {
      this.leftSideEl.nativeElement.style.setProperty('--contact-form-left-title-font-weight', d.leftTitleFontWeight.value);
    }
    if (d.rightTitleSize && d.rightTitleSize.value) {
      this.rightSideEl.nativeElement.style.setProperty('--contact-form-right-title-size', d.rightTitleSize.value + 'px');
    }
    if (d.rightTitleFontWeight && d.rightTitleFontWeight.value) {
      this.rightSideEl.nativeElement.style.setProperty('--contact-form-right-title-font-weight', d.rightTitleFontWeight.value);
    }
  }

  private setPanelBackgroundImage(d: IContactFormPanelData) {
    if (d.backgroundImage && d.backgroundImage.value && d.backgroundImage.value.src) {
      this.panelElRef.nativeElement.style.backgroundImage = `url(${d.backgroundImage.value.src})`;
    }
  }
}
